/**
 * ProjectSharing.js
 * Lightweight share dialog for individual projects.
 * Shows who has access and lets admins/owners share with org members.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../utils/ThemeSwitcher';
import {
  Share2, Users, UserPlus, X, Check, Eye, Pencil, Zap,
  Shield, Crown, RefreshCw, Trash2
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const FONT = "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif";

function authHeaders() {
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  const token = localStorage.getItem('token');
  return {
    'Authorization': token ? `Bearer ${token}` : '',
    'user_email': userInfo?.email || '',
    'Content-Type': 'application/json',
  };
}

const PERM_CONFIG = {
  manage: { label: 'Can Manage', icon: Shield, color: '#8B5CF6', desc: 'Configure detectors, edit settings' },
  run:    { label: 'Can Run',    icon: Zap,    color: '#3B82F6', desc: 'Run checks, export data' },
  view:   { label: 'Can View',   icon: Eye,    color: '#6B7280', desc: 'View dashboards only' },
};

const ProjectSharing = ({ projectId, projectName, isOpen, onClose }) => {
  const { isDarkMode } = useTheme();
  const [shares, setShares] = useState([]);
  const [orgMembers, setOrgMembers] = useState([]);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedPerm, setSelectedPerm] = useState('view');
  const [saving, setSaving] = useState(false);

  const colors = {
    bg: isDarkMode ? '#141516' : '#FFFFFF',
    border: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)',
    text: isDarkMode ? '#E2E8F0' : '#1E293B',
    textSec: isDarkMode ? '#64748B' : '#94A3B8',
    accent: '#38B6FF',
    inputBg: isDarkMode ? '#1A1B1C' : '#F9FAFB',
    inputBorder: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)',
    danger: '#EF4444',
    success: '#10B981',
  };

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      // Fetch shares
      const sharesRes = await fetch(`${API_BASE_URL}/api/team/projects/${projectId}/shares`, { headers: authHeaders() });
      const sharesData = await sharesRes.json();
      setShares(sharesData.shares || []);

      // Fetch org + members
      const orgRes = await fetch(`${API_BASE_URL}/api/team/organizations/mine`, { headers: authHeaders() });
      const orgData = await orgRes.json();
      if (orgData.organization) {
        setOrg(orgData.organization);
        const membersRes = await fetch(`${API_BASE_URL}/api/team/organizations/${orgData.organization.id}/members`, { headers: authHeaders() });
        const membersData = await membersRes.json();
        setOrgMembers(membersData.members || []);
      }
    } catch (e) {
      console.error('Failed to fetch sharing data:', e);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (isOpen) fetchData();
  }, [isOpen, fetchData]);

  const shareProject = async () => {
    if (!selectedUserId) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/team/projects/${projectId}/share`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ user_id: selectedUserId, permission: selectedPerm }),
      });
      if (res.ok) {
        setSelectedUserId('');
        fetchData();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const removeShare = async (userId) => {
    try {
      await fetch(`${API_BASE_URL}/api/team/projects/${projectId}/shares/${userId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      fetchData();
    } catch (e) {
      console.error(e);
    }
  };

  if (!isOpen) return null;

  // Members not yet shared with
  const sharedUserIds = new Set(shares.map(s => s.user_id));
  const availableMembers = orgMembers.filter(m => !sharedUserIds.has(m.user_id));

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)',
      fontFamily: FONT,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        width: '100%', maxWidth: '480px',
        background: colors.bg, borderRadius: '16px',
        border: `1px solid ${colors.border}`,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: `1px solid ${colors.border}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Share2 size={18} color={colors.accent} />
            <div>
              <div style={{ fontSize: '16px', fontWeight: '600', color: colors.text }}>Share Project</div>
              <div style={{ fontSize: '12px', color: colors.textSec }}>{projectName}</div>
            </div>
          </div>
          <button onClick={onClose} style={{
            width: '32px', height: '32px', borderRadius: '8px',
            border: `1px solid ${colors.border}`, background: 'transparent',
            color: colors.textSec, cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '16px 20px', maxHeight: '60vh', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '30px' }}>
              <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite', color: colors.textSec }} />
            </div>
          ) : (
            <>
              {/* Add people */}
              {org && availableMembers.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '500', color: colors.textSec, marginBottom: '8px' }}>
                    Add team members
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <select
                      value={selectedUserId}
                      onChange={e => setSelectedUserId(e.target.value)}
                      style={{
                        flex: 1, padding: '8px 12px', borderRadius: '8px',
                        border: `1px solid ${colors.inputBorder}`, background: colors.inputBg,
                        color: colors.text, fontSize: '13px', fontFamily: FONT,
                      }}
                    >
                      <option value="">Select member...</option>
                      {availableMembers.map(m => (
                        <option key={m.user_id} value={m.user_id}>
                          {m.name || m.email}
                        </option>
                      ))}
                    </select>
                    <select
                      value={selectedPerm}
                      onChange={e => setSelectedPerm(e.target.value)}
                      style={{
                        padding: '8px 10px', borderRadius: '8px',
                        border: `1px solid ${colors.inputBorder}`, background: colors.inputBg,
                        color: colors.text, fontSize: '13px', fontFamily: FONT, width: '110px',
                      }}
                    >
                      <option value="view">View</option>
                      <option value="run">Run</option>
                      <option value="manage">Manage</option>
                    </select>
                    <button
                      onClick={shareProject}
                      disabled={!selectedUserId || saving}
                      style={{
                        padding: '8px 14px', borderRadius: '8px', border: 'none',
                        background: colors.accent, color: '#FFF', fontSize: '13px',
                        fontWeight: '600', cursor: 'pointer', fontFamily: FONT,
                        opacity: !selectedUserId || saving ? 0.5 : 1,
                      }}
                    >
                      {saving ? '...' : 'Add'}
                    </button>
                  </div>
                </div>
              )}

              {!org && (
                <div style={{ padding: '20px', textAlign: 'center', color: colors.textSec, fontSize: '13px' }}>
                  Create an organization first to share projects with team members.
                </div>
              )}

              {/* Current shares */}
              {shares.length > 0 && (
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '500', color: colors.textSec, marginBottom: '8px' }}>
                    People with access
                  </div>
                  {shares.map(s => {
                    const perm = PERM_CONFIG[s.permission] || PERM_CONFIG.view;
                    const PermIcon = perm.icon;
                    return (
                      <div key={s.id} style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '10px 0', borderBottom: `1px solid ${colors.border}`,
                      }}>
                        <div style={{
                          width: '32px', height: '32px', borderRadius: '8px',
                          background: `${perm.color}12`, display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                        }}>
                          <span style={{ fontSize: '13px', fontWeight: '600', color: perm.color }}>
                            {(s.name || s.email)?.[0]?.toUpperCase()}
                          </span>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', fontWeight: '500', color: colors.text }}>
                            {s.name || s.email.split('@')[0]}
                          </div>
                          <div style={{ fontSize: '11px', color: colors.textSec }}>{s.email}</div>
                        </div>
                        <span style={{
                          fontSize: '11px', fontWeight: '600', color: perm.color,
                          display: 'flex', alignItems: 'center', gap: '4px',
                        }}>
                          <PermIcon size={12} /> {perm.label}
                        </span>
                        <button onClick={() => removeShare(s.user_id)} style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: colors.textSec, padding: '4px',
                        }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {shares.length === 0 && org && (
                <div style={{ padding: '20px', textAlign: 'center', color: colors.textSec, fontSize: '13px' }}>
                  This project hasn't been shared with anyone yet.
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default ProjectSharing;